import React, { useState } from "react";
import {
  Stack,
  Group,
  Text,
  ActionIcon,
  TextInput,
  Button,
  Box,
} from "@mantine/core";
import { IconChevronLeft, IconExternalLink } from "@tabler/icons-react";
import { useNavigate } from "react-router-dom";
import CustomRichTextEditor from "../../common/RichTextEditor";
import {
  hasFieldError,
  getFieldErrorMessage,
  getInputStyles,
  getLabelStyles,
  getLabelText,
} from "../utils/validation";

interface ServicesContent {
  title?: string;
  subtitle?: string;
  subtext?: string;
  menuNote?: string;
}

interface WebsiteGeneratorServicesContentProps {
  servicesContent?: ServicesContent;
  onContentChange: (content: ServicesContent) => void;
  onBack: () => void;
  missingFields?: string[];
  // Validation prop for field-level highlighting
  requiredValidation?: { [field: string]: boolean };
}

const WebsiteGeneratorServicesContent: React.FC<
  WebsiteGeneratorServicesContentProps
> = ({
  servicesContent,
  onContentChange,
  onBack,
  missingFields = [],
  requiredValidation = {},
}) => {
  const navigate = useNavigate();
  const [validationErrors, setValidationErrors] = useState<
    Record<string, string>
  >({});

  // Handle input changes
  const handleInputChange = (field: keyof ServicesContent, value: string) => {
    // Validate character limit
    if (value.length > 100) {
      setValidationErrors((prev) => ({
        ...prev,
        [field]: `${field} cannot exceed 100 characters`,
      }));
      return;
    }

    // Clear validation error
    setValidationErrors((prev) => {
      const newErrors = { ...prev };
      delete newErrors[field];
      return newErrors;
    });

    // Update content immediately
    onContentChange({
      ...(servicesContent || {}),
      [field]: value,
    });
  };

  return (
    <Stack gap="md">
      {/* Header */}
      <Group justify="space-between" align="center">
        <ActionIcon variant="subtle" onClick={onBack}>
          <IconChevronLeft size={16} />
        </ActionIcon>
        <Text fw={600} size="lg">
          Services
        </Text>
      </Group>

      {/* Section Title */}
      <Box>
        <Text
          size="sm"
          fw={500}
          mb="xs"
          style={getLabelStyles(
            "title",
            requiredValidation,
            servicesContent?.title,
          )}
        >
          {getLabelText(
            "Section Title",
            "title",
            requiredValidation,
            servicesContent?.title,
          )}
        </Text>
        <TextInput
          placeholder="Our Services"
          value={servicesContent?.title || ""}
          onChange={(event) =>
            handleInputChange("title", event.currentTarget.value)
          }
          error={getFieldErrorMessage(
            "title",
            requiredValidation,
            validationErrors,
            servicesContent?.title,
          )}
          maxLength={100}
          styles={getInputStyles(
            "title",
            requiredValidation,
            missingFields,
            "our_services.header",
            servicesContent?.title,
          )}
        />
      </Box>

      {/* Subtitle */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Subtitle
        </Text>
        <TextInput
          placeholder="Enter subtitle..."
          value={servicesContent?.subtitle || ""}
          onChange={(event) =>
            handleInputChange("subtitle", event.currentTarget.value)
          }
          error={validationErrors.subtitle}
          maxLength={200}
        />
      </Box>

      {/* Subtext */}
      <Box>
        <CustomRichTextEditor
          label="Subtext"
          placeholder="Enter subtext content..."
          value={servicesContent?.subtext || ""}
          onChange={(htmlValue) =>
            onContentChange({
              ...(servicesContent || {}),
              subtext: htmlValue,
            })
          }
          minHeight={150}
        />
      </Box>

      {/* Menu Note */}
      <Box>
        <Text size="sm" fw={500} mb="xs">
          Menu Note
        </Text>
        <TextInput
          placeholder="Pricing note shown above service menu..."
          value={servicesContent?.menuNote || ""}
          onChange={(event) =>
            handleInputChange("menuNote", event.currentTarget.value)
          }
          error={validationErrors.menuNote}
          maxLength={200}
        />
        <Text size="xs" c="dimmed" mt="xs">
          Pricing note shown above service menu
        </Text>
      </Box>

      {/* Settings Link */}
      <Group gap="xs" mt="md">
        <Text size="sm" c="dimmed">
          Visit
        </Text>
        <Button
          variant="subtle"
          color="blue"
          size="sm"
          rightSection={<IconExternalLink size={14} />}
          onClick={() => navigate("/admin/settings/services")}
        >
          Settings &gt; Services
        </Button>
        <Text size="sm" c="dimmed">
          to edit services.
        </Text>
      </Group>
    </Stack>
  );
};

export default WebsiteGeneratorServicesContent;
